import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Shield, UserPlus, Lock, Unlock, Eye, Edit, Trash2, 
  Search, Users, Key, AlertTriangle, CheckCircle2, XCircle,
  Clock, Calendar, RefreshCw, ShieldAlert, ShieldCheck,
  FileText, User, Building2
} from 'lucide-react';
import { toast } from 'sonner';

// Resource types that can be granted access
const RESOURCE_TYPES = [
  // Clinical Access
  { value: 'clinical', label: 'All Clinical Data', description: 'Assessments, progress notes, evaluations', category: 'Clinical' },
  { value: 'patient', label: 'Specific Patient', description: 'Access to a particular patient\'s records', category: 'Clinical' },
  
  // Form Configuration
  { value: 'form_config', label: 'Form Configuration', description: 'Add/remove sections and fields in assessment, evaluation, SOAP forms', category: 'Forms' },
  { value: 'assessment_form_edit', label: 'Assessment Form Edit', description: 'Add or remove sections in Assessment forms', category: 'Forms' },
  { value: 'evaluation_form_edit', label: 'Evaluation Form Edit', description: 'Add or remove sections in Evaluation forms', category: 'Forms' },
  { value: 'soap_form_edit', label: 'SOAP Note Form Edit', description: 'Add or remove sections in Progress/SOAP notes', category: 'Forms' },
  
  // Finance Access
  { value: 'finance', label: 'Finance Data', description: 'Invoices, payments, financial reports', category: 'Finance' },
  { value: 'invoice_delete', label: 'Delete Invoices', description: 'Permission to permanently delete invoices', category: 'Finance' },
  { value: 'accounts_excel', label: 'Accounts Excel', description: 'Spreadsheet and financial data', category: 'Finance' },
  { value: 'payroll', label: 'Payroll Data', description: 'Staff salaries and payslips', category: 'Finance' },
  { value: 'earnings_delete', label: 'Delete Earnings Records', description: 'Permission to delete session/earnings records', category: 'Finance' },
  { value: 'payslip_delete', label: 'Delete Payslips', description: 'Permission to delete professional payslips (My Earnings)', category: 'Finance' },
  
  // HR & Policy Management
  { value: 'hr_policies', label: 'HR Policy Management', description: 'Add, edit, delete, download, print, email policies', category: 'HR' },
  { value: 'hr_policy_create', label: 'Create HR Policies', description: 'Add new company policies', category: 'HR' },
  { value: 'hr_policy_edit', label: 'Edit HR Policies', description: 'Modify existing company policies', category: 'HR' },
  { value: 'hr_policy_delete', label: 'Delete HR Policies', description: 'Remove company policies', category: 'HR' },
  { value: 'hr_leave_approve', label: 'Approve Leave Requests', description: 'Approve or reject staff leave requests', category: 'HR' },
  { value: 'hr_training_manage', label: 'Manage Training Records', description: 'Add and update staff training records', category: 'HR' },
  
  // Safeguarding
  { value: 'safeguarding_policy', label: 'Safeguarding Policy Management', description: 'Add, edit, delete safeguarding policies', category: 'Safeguarding' },
  { value: 'safeguarding_dbs', label: 'DBS Records Management', description: 'Manage staff DBS check records', category: 'Safeguarding' },
  
  // Parent Portal
  { value: 'parent_portal', label: 'Parent Portal Management', description: 'Manage parent accounts and messages', category: 'Portal' },
  
  // Administration
  { value: 'staff_delete', label: 'Delete Staff', description: 'Permission to delete staff from directory', category: 'Admin' },
  { value: 'hr', label: 'HR & Workforce', description: 'Leave, training, DBS records', category: 'Admin' },
  { value: 'audit', label: 'Audit Logs', description: 'System activity logs', category: 'Admin' },
  { value: 'admin', label: 'Admin Panel', description: 'User management and settings', category: 'Admin' },
];

// Role descriptions for reference
const ROLE_INFO = {
  director: { name: 'Director', color: 'bg-purple-100 text-purple-800' },
  therapist: { name: 'Therapist', color: 'bg-blue-100 text-blue-800' },
  professional: { name: 'Professional', color: 'bg-cyan-100 text-cyan-800' },
  clinical_lead: { name: 'Clinical Lead', color: 'bg-teal-100 text-teal-800' },
  educational_lead: { name: 'Educational Lead', color: 'bg-green-100 text-green-800' },
  finance: { name: 'Finance', color: 'bg-amber-100 text-amber-800' },
  admin: { name: 'Admin Staff', color: 'bg-slate-100 text-slate-800' },
  hr: { name: 'HR Staff', color: 'bg-pink-100 text-pink-800' },
  support: { name: 'Reception/Support', color: 'bg-orange-100 text-orange-800' },
};

export default function AccessManagementPage() {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('grants');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [grants, setGrants] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [users, setUsers] = useState([]);
  const [patients, setPatients] = useState([]);
  
  // Dialog states
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  
  // Form states
  const [grantForm, setGrantForm] = useState({
    user_id: '',
    user_email: '',
    resource_type: '',
    resource_id: '',
    can_view: true,
    can_edit: false,
    reason: '',
    expires_at: ''
  });
  
  const [blockForm, setBlockForm] = useState({
    user_id: '',
    user_email: '',
    resource_type: '',
    reason: ''
  });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Check if current user is super admin
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  const fetchData = useCallback(async () => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [grantsRes, blocksRes, usersRes, patientsRes] = await Promise.all([
        api.get('/auth/access-grants').catch(() => ({ data: [] })),
        api.get('/auth/access-blocks').catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
        api.get('/patients').catch(() => ({ data: [] }))
      ]);
      
      setGrants(grantsRes.data || []);
      setBlocks(blocksRes.data || []);
      setUsers(usersRes.data || []);
      setPatients(patientsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load access management data');
    } finally {
      setLoading(false);
    }
  }, [api, isSuperAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Grant handlers
  const openNewGrant = () => {
    setEditingGrant(null);
    setGrantForm({
      user_id: '',
      user_email: '',
      resource_type: '',
      resource_id: '',
      can_view: true,
      can_edit: false,
      reason: '',
      expires_at: ''
    });
    setShowGrantDialog(true);
  };

  const handleUserSelect = (userId, formType = 'grant') => {
    const selectedUser = users.find(u => u.id === userId);
    if (selectedUser) {
      if (formType === 'grant') {
        setGrantForm(prev => ({
          ...prev,
          user_id: userId,
          user_email: selectedUser.email
        }));
      } else {
        setBlockForm(prev => ({
          ...prev,
          user_id: userId,
          user_email: selectedUser.email
        }));
      }
    }
  };

  const saveGrant = async () => {
    if (!grantForm.user_id || !grantForm.resource_type) {
      toast.error('Please select a user and resource type');
      return;
    }

    try {
      await api.post('/auth/access-grants', grantForm);
      toast.success('Access grant created successfully');
      setShowGrantDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create access grant');
    }
  };

  const revokeGrant = async (grantId) => {
    if (!confirm('Are you sure you want to revoke this access grant?')) return;
    
    try {
      await api.delete(`/auth/access-grants/${grantId}`);
      toast.success('Access grant revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke grant');
    }
  };

  // Block handlers
  const openNewBlock = () => {
    setBlockForm({
      user_id: '',
      user_email: '',
      resource_type: '',
      reason: ''
    });
    setShowBlockDialog(true);
  };

  const saveBlock = async () => {
    if (!blockForm.user_id) {
      toast.error('Please select a user');
      return;
    }

    try {
      await api.post('/auth/access-blocks', blockForm);
      toast.success('Access block created');
      setShowBlockDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create access block');
    }
  };

  const removeBlock = async (blockId) => {
    if (!confirm('Are you sure you want to remove this access block?')) return;
    
    try {
      await api.delete(`/auth/access-blocks/${blockId}`);
      toast.success('Access block removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove block');
    }
  };

  const getRoleBadge = (role) => {
    const info = ROLE_INFO[role] || { name: role, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={info.color}>{info.name}</Badge>;
  };

  const getResourceLabel = (type) => {
    return RESOURCE_TYPES.find(r => r.value === type)?.label || type;
  };

  // Filter users based on search
  const filteredUsers = users.filter(u => 
    `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="access-denied-page">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <ShieldAlert className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
        <p className="text-slate-500 text-center max-w-md">
          Only the Super Admin (Md Ashraful Alam Sujan) can access the Access Management panel.
        </p>
        <Button onClick={() => window.history.back()} className="mt-6">
          Go Back
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="access-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Shield className="w-8 h-8" />
            Access Management
          </h1>
          <p className="text-slate-500 mt-1">
            Super Admin Control Panel - Grant or block access for any user
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={openNewGrant} className="bg-green-600 hover:bg-green-700">
            <Unlock className="w-4 h-4 mr-2" />
            Grant Access
          </Button>
          <Button onClick={openNewBlock} variant="destructive">
            <Lock className="w-4 h-4 mr-2" />
            Block Access
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Super Admin Powers:</strong> You can grant or revoke access for any user to any part of the system.
          Access grants override default role-based permissions. Blocks prevent all access to specified resources.
        </AlertDescription>
      </Alert>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Grants</p>
                <p className="text-3xl font-bold text-green-600">{grants.length}</p>
              </div>
              <ShieldCheck className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Active Blocks</p>
                <p className="text-3xl font-bold text-red-600">{blocks.length}</p>
              </div>
              <ShieldAlert className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Users</p>
                <p className="text-3xl font-bold text-[#1e3a5f]">{users.length}</p>
              </div>
              <Users className="w-10 h-10 text-slate-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Patients</p>
                <p className="text-3xl font-bold text-[#2a9d8f]">{patients.length}</p>
              </div>
              <User className="w-10 h-10 text-teal-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="grants" data-testid="tab-grants">
            <Unlock className="w-4 h-4 mr-2" />
            Access Grants
          </TabsTrigger>
          <TabsTrigger value="blocks" data-testid="tab-blocks">
            <Lock className="w-4 h-4 mr-2" />
            Access Blocks
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            User Overview
          </TabsTrigger>
        </TabsList>

        {/* Access Grants Tab */}
        <TabsContent value="grants" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-green-600" />
                    Active Access Grants
                  </CardTitle>
                  <CardDescription>
                    Special access permissions granted to users beyond their default role
                  </CardDescription>
                </div>
                <Button onClick={openNewGrant} size="sm" className="bg-green-600 hover:bg-green-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  New Grant
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {grants.length === 0 ? (
                <div className="text-center py-8">
                  <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active access grants</p>
                  <p className="text-sm text-slate-400 mt-1">Grant special access to users who need it</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Permissions</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Granted By</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grants.map((grant) => (
                      <TableRow key={grant.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{grant.user_email}</p>
                            <p className="text-xs text-slate-500">{grant.user_id?.slice(0, 8)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getResourceLabel(grant.resource_type)}</Badge>
                          {grant.resource_id && (
                            <p className="text-xs text-slate-500 mt-1">ID: {grant.resource_id?.slice(0, 8)}...</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {grant.can_view && <Badge className="bg-blue-100 text-blue-800">View</Badge>}
                            {grant.can_edit && <Badge className="bg-amber-100 text-amber-800">Edit</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-600 max-w-[200px] truncate">{grant.reason || '-'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{grant.granted_by_name}</p>
                          <p className="text-xs text-slate-500">{new Date(grant.granted_at).toLocaleDateString()}</p>
                        </TableCell>
                        <TableCell>
                          {grant.expires_at ? (
                            <p className="text-sm">{new Date(grant.expires_at).toLocaleDateString()}</p>
                          ) : (
                            <span className="text-slate-400">Never</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => revokeGrant(grant.id)}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access Blocks Tab */}
        <TabsContent value="blocks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-600" />
                    Active Access Blocks
                  </CardTitle>
                  <CardDescription>
                    Users blocked from accessing specific resources
                  </CardDescription>
                </div>
                <Button onClick={openNewBlock} size="sm" variant="destructive">
                  <Lock className="w-4 h-4 mr-2" />
                  New Block
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {blocks.length === 0 ? (
                <div className="text-center py-8">
                  <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active access blocks</p>
                  <p className="text-sm text-slate-400 mt-1">Block users from specific resources when needed</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Resource Blocked</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Blocked By</TableHead>
                      <TableHead>Blocked At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blocks.map((block) => (
                      <TableRow key={block.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{block.user_email}</p>
                            <p className="text-xs text-slate-500">{block.user_id?.slice(0, 8)}...</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {block.resource_type ? (
                            <Badge className="bg-red-100 text-red-800">{getResourceLabel(block.resource_type)}</Badge>
                          ) : (
                            <Badge className="bg-red-200 text-red-900">ALL ACCESS</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-600 max-w-[200px] truncate">{block.reason || '-'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{block.blocked_by_name}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{new Date(block.blocked_at).toLocaleDateString()}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-green-600 hover:text-green-800"
                            onClick={() => removeBlock(block.id)}
                          >
                            <Unlock className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Overview Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    User Overview
                  </CardTitle>
                  <CardDescription>
                    All users and their roles - click to manage access
                  </CardDescription>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Grants</TableHead>
                    <TableHead>Blocks</TableHead>
                    <TableHead className="text-right">Quick Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => {
                    const userGrants = grants.filter(g => g.user_id === u.id);
                    const userBlocks = blocks.filter(b => b.user_id === u.id);
                    
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">
                          {u.first_name} {u.last_name}
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell>
                          {u.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-800">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {userGrants.length > 0 ? (
                            <Badge className="bg-green-100 text-green-800">{userGrants.length}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {userBlocks.length > 0 ? (
                            <Badge className="bg-red-100 text-red-800">{userBlocks.length}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-green-600"
                              onClick={() => {
                                handleUserSelect(u.id, 'grant');
                                setShowGrantDialog(true);
                              }}
                              title="Grant Access"
                            >
                              <Unlock className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => {
                                handleUserSelect(u.id, 'block');
                                setShowBlockDialog(true);
                              }}
                              title="Block Access"
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Grant Access Dialog */}
      <Dialog open={showGrantDialog} onOpenChange={setShowGrantDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-600" />
              Grant Special Access
            </DialogTitle>
            <DialogDescription>
              Give a user access to resources beyond their default role permissions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* User Selection */}
            <div>
              <Label className="mb-2">Select User *</Label>
              <Select value={grantForm.user_id} onValueChange={(v) => handleUserSelect(v, 'grant')}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resource Type */}
            <div>
              <Label className="mb-2">Resource Type *</Label>
              <Select value={grantForm.resource_type} onValueChange={(v) => setGrantForm(prev => ({ ...prev, resource_type: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select resource..." />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>
                      <div>
                        <p className="font-medium">{rt.label}</p>
                        <p className="text-xs text-slate-500">{rt.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Specific Resource (Patient) */}
            {grantForm.resource_type === 'patient' && (
              <div>
                <Label className="mb-2">Select Specific Patient</Label>
                <Select value={grantForm.resource_id} onValueChange={(v) => setGrantForm(prev => ({ ...prev, resource_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a patient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name} ({p.reg_no})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Permissions */}
            <div>
              <Label className="mb-2">Permissions</Label>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="can_view" 
                    checked={grantForm.can_view} 
                    onCheckedChange={(c) => setGrantForm(prev => ({ ...prev, can_view: c }))}
                  />
                  <label htmlFor="can_view" className="text-sm flex items-center gap-1">
                    <Eye className="w-4 h-4" /> View
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="can_edit" 
                    checked={grantForm.can_edit} 
                    onCheckedChange={(c) => setGrantForm(prev => ({ ...prev, can_edit: c }))}
                  />
                  <label htmlFor="can_edit" className="text-sm flex items-center gap-1">
                    <Edit className="w-4 h-4" /> Edit
                  </label>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div>
              <Label className="mb-2">Reason for Grant</Label>
              <Textarea
                value={grantForm.reason}
                onChange={(e) => setGrantForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Why is this access being granted?"
                rows={2}
              />
            </div>

            {/* Expiration */}
            <div>
              <Label className="mb-2">Expiration Date (Optional)</Label>
              <Input
                type="date"
                value={grantForm.expires_at}
                onChange={(e) => setGrantForm(prev => ({ ...prev, expires_at: e.target.value }))}
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for permanent access</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGrantDialog(false)}>Cancel</Button>
            <Button onClick={saveGrant} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Access Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              Block User Access
            </DialogTitle>
            <DialogDescription>
              Prevent a user from accessing specific resources or the entire system
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* User Selection */}
            <div>
              <Label className="mb-2">Select User *</Label>
              <Select value={blockForm.user_id} onValueChange={(v) => handleUserSelect(v, 'block')}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resource Type */}
            <div>
              <Label className="mb-2">Resource to Block</Label>
              <Select value={blockForm.resource_type || 'all_access'} onValueChange={(v) => setBlockForm(prev => ({ ...prev, resource_type: v === 'all_access' ? '' : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select resource to block" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_access">
                    <div className="text-red-600 font-medium">Block ALL Access</div>
                  </SelectItem>
                  {RESOURCE_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>
                      <div>
                        <p className="font-medium">{rt.label}</p>
                        <p className="text-xs text-slate-500">{rt.description}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Select &quot;Block ALL Access&quot; to block the user from the entire system</p>
            </div>

            {/* Reason */}
            <div>
              <Label className="mb-2">Reason for Block *</Label>
              <Textarea
                value={blockForm.reason}
                onChange={(e) => setBlockForm(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Document why this user is being blocked..."
                rows={3}
              />
            </div>

            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <strong>Warning:</strong> Blocked users will be immediately denied access to the specified resources.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>Cancel</Button>
            <Button onClick={saveBlock} variant="destructive">
              <Lock className="w-4 h-4 mr-2" />
              Block Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
